import { CloseRounded, ExpandMoreRounded } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { apiGet, displayLabel } from '../../perps-v1-shared/api';
import { MarketSector, MarketView, PinCandidate } from '../../perps-v1-shared/types';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { viewsApi } from '../dataProvider';
import {
  FormState,
  NATIVE_DEX,
  SORT_DIRECTIONS,
  SORT_DIRECTION_LABEL_KEYS,
  SORT_FIELDS,
  SORT_FIELD_LABEL_KEYS,
  blankForm,
  buildViewBody,
  formFromView,
} from '../viewForm';
import ViewAvailableSymbols from './ViewAvailableSymbols';
import ViewMembersEditor from './ViewMembersEditor';
import ViewPreviewPanel from './ViewPreviewPanel';

interface Props {
  open: boolean;
  view: MarketView | null;
  onClose: () => void;
  onSaved: () => void;
}

// GET /perps-v1/views/dexes 的一行：dex 是规则中使用的原始值（空字符串代表 native），
// id 仅在 dex === '' 时固定为 'native'（因为 '' 不能用作 React key），其余行 id 与 dex 相同。
interface DexOption {
  id: string;
  dex: string;
}

// Content & order only: dexes/sectors/filters, sort, include/exclude membership and the live
// preview that validates them. Edit-only — never used to create a view (basic navigation info —
// code/label/badge/parent/display order/limit — lives in ViewEditorDialog). Both dialogs seed the
// SAME full FormState from `formFromView` and save with the SAME `buildViewBody`, so saving from
// here still sends every field ViewEditorDialog owns — see viewForm.ts.
export default function ViewSymbolsDialog({ open, view, onClose, onSaved }: Props) {
  const translate = useTranslate();
  const notify = useNotify();

  const [form, setForm] = useState<FormState>(view ? formFromView(view) : blankForm());
  const [sectors, setSectors] = useState<MarketSector[]>([]);
  const [dexOptions, setDexOptions] = useState<DexOption[]>([]);
  const [candidates, setCandidates] = useState<PinCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesFailed, setCandidatesFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Full pre-exclude match set (pinned + manual + rule) from the last successful preview fetch —
  // reported by ViewPreviewPanel, see its `onResult`. Used only to compute the Available (include)
  // and Exclude candidate pools below; the live preview table renders its own `data`, independent
  // of this.
  const [matchedSymbols, setMatchedSymbols] = useState<string[]>([]);
  // Bumped on every discrete add/remove/reorder so ViewPreviewPanel refetches immediately instead
  // of waiting out the 600ms debounce — see decision D13 step 5.
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (open) {
      setForm(view ? formFromView(view) : blankForm());
      setMatchedSymbols([]);
      setRefreshNonce(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view]);

  // `active` 是每次 setup 各自新建的闭包变量，StrictMode 的 setup->cleanup->setup 双调用下
  // 也总是重新置为 true（不能用只在 cleanup 里清零的 ref），并保证快速开关弹窗时旧响应不会
  // 覆盖新一次的结果。
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    apiGet<{ data: MarketSector[]; total: number }>('/perps-v1/sectors')
      .then((json) => {
        if (active) setSectors(json?.data ?? []);
      })
      .catch(() => {
        if (active) setSectors([]);
      });
    return () => {
      active = false;
    };
  }, [open]);

  // Live dex list, degrades to an empty list on failure — never blocks the dialog. The checkbox
  // render below still merges in any dex the view already has configured (see `dexRows`), so a
  // rule referencing a dex this fetch fails to return is never silently un-rendered.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    apiGet<{ data: DexOption[]; total: number }>('/perps-v1/views/dexes')
      .then((json) => {
        if (active) setDexOptions(json?.data ?? []);
      })
      .catch((error: any) => {
        if (!active) return;
        setDexOptions([]);
        notify(
          errorMessage(error, translate('module.perpsV1Views.dexesLoadError')),
          { type: 'error' }
        );
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Candidate pool for the include/exclude pickers — loaded once per dialog open, not per
  // keystroke. On failure the editor still renders already-selected symbols (never lost),
  // it just can't offer new ones to add until reopened.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setCandidatesLoading(true);
    setCandidatesFailed(false);
    apiGet<{ data: PinCandidate[]; total: number }>('/perps-v1/pins/candidates?page=1&size=1000')
      .then((json) => {
        if (!active) return;
        setCandidates(json?.data ?? []);
      })
      .catch((error: any) => {
        if (!active) return;
        setCandidates([]);
        setCandidatesFailed(true);
        notify(
          errorMessage(error, translate('module.perpsV1Views.membersLoadError')),
          { type: 'error' }
        );
      })
      .finally(() => {
        if (active) setCandidatesLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Same payload handleSave would send — recomputed on every form change. The preview panel
  // debounces when it actually POSTs this (see previewTriggerKey below); this memo itself does
  // no I/O.
  const previewBody = useMemo(() => buildViewBody(form), [form]);

  // Debounce trigger for the live preview — narrowed to fields that can actually change which
  // symbols show up or their order (code/dexes/sectorCodes/include/exclude/sort/listing
  // window/min volume/min change %). Deliberately excludes label/badge/parentCode/displayOrder/
  // displayLimit — none of those affect membership or ordering, so editing them (in the other
  // dialog) must never fire a request. Serialised to a single primitive string (not the raw field
  // values) so the panel's auto-refresh effect sees a STABLE dependency across renders that don't
  // touch any of these fields.
  const previewTriggerKey = useMemo(
    () =>
      JSON.stringify({
        code: form.code.trim(),
        dexes: form.dexes,
        sectorCodes: form.sectorCodes,
        includeSymbols: form.includeSymbols,
        excludeSymbols: form.excludeSymbols,
        sortField: form.sortField,
        sortDirection: form.sortDirection,
        listingWindowDays: form.listingWindowDays,
        minVolume: form.minVolume,
        minChangePercent: form.minChangePercent,
      }),
    [
      form.code,
      form.dexes,
      form.sectorCodes,
      form.includeSymbols,
      form.excludeSymbols,
      form.sortField,
      form.sortDirection,
      form.listingWindowDays,
      form.minVolume,
      form.minChangePercent,
    ]
  );

  const sectorOptions = useMemo(
    () => sectors.map((s) => ({ code: s.code, label: s.name?.en || s.name?.slug || s.code })),
    [sectors]
  );
  const selectedSectorOptions = useMemo(
    () => sectorOptions.filter((o) => form.sectorCodes.includes(o.code)),
    [sectorOptions, form.sectorCodes]
  );

  // Rows to render as checkboxes: the fetched list, plus any dex the view's rule already carries
  // that the fetch hasn't (yet, or ever) returned — while `dexOptions` is still loading or failed
  // to load, this keeps an existing rule's checkboxes visible instead of silently un-rendering
  // them. `''` can't be a React key, so a synthetic row for it uses 'native' like the API does.
  const dexRows = useMemo(() => {
    const known = new Set(dexOptions.map((o) => o.dex));
    const extra = form.dexes
      .filter((d) => !known.has(d))
      .map((d) => ({ id: d === NATIVE_DEX ? 'native' : d, dex: d }));
    return [...dexOptions, ...extra];
  }, [dexOptions, form.dexes]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleDex = (dex: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      dexes: checked
        ? prev.dexes.includes(dex)
          ? prev.dexes
          : [...prev.dexes, dex]
        : prev.dexes.filter((d) => d !== dex),
    }));
  };

  // 候选池不可用（加载中或拉取失败）时无从判断某个手动加入的 symbol 是否已下架，
  // 一律不打「已下架」标记，避免把「拉取失败」误报成「币种已下架」（与 ViewMembersEditor
  // 的 candidatesUnavailable 同一约定）。
  const candidatesUnavailable = candidatesLoading || candidatesFailed;
  const candidateSet = useMemo(() => new Set(candidates.map((c) => c.symbol)), [candidates]);
  const matchedSet = useMemo(() => new Set(matchedSymbols), [matchedSymbols]);
  const excludeSet = useMemo(() => new Set(form.excludeSymbols), [form.excludeSymbols]);
  const includeSet = useMemo(() => new Set(form.includeSymbols), [form.includeSymbols]);

  // Manually included symbols with no candidate AND no match — delisted since they were
  // configured. Upstream drops these from the preview entirely, so without this they'd be
  // invisible and unremovable — rendered in their own block above the live preview instead.
  const delistedManual = useMemo(() => {
    if (candidatesUnavailable) return [];
    return form.includeSymbols.filter((s) => !matchedSet.has(s) && !candidateSet.has(s));
  }, [form.includeSymbols, matchedSet, candidateSet, candidatesUnavailable]);
  const delistedManualSet = useMemo(() => new Set(delistedManual), [delistedManual]);

  // Manual symbols still eligible to render inside the live preview table (i.e. not delisted) —
  // anchors the manual block's order there and drives the up/down reorder bounds.
  const activeManualSymbols = useMemo(
    () => form.includeSymbols.filter((s) => !delistedManualSet.has(s)),
    [form.includeSymbols, delistedManualSet]
  );

  // Left column: candidates minus anything already part of the view by any means (matched by the
  // rule/pinned/manual) and minus anything excluded — "one symbol, one place" (decision D13).
  const availableCandidates = useMemo(
    () => candidates.filter((c) => !matchedSet.has(c.symbol) && !includeSet.has(c.symbol) && !excludeSet.has(c.symbol)),
    [candidates, matchedSet, includeSet, excludeSet]
  );

  // Exclude's candidate pool = what the rule actually caught (matchedSymbols, pre-exclude), not
  // the whole universe — you can only remove what is currently in the view. ViewMembersEditor
  // itself further subtracts already-excluded (`value`) and includeSymbols (`unavailableSymbols`).
  const excludeCandidates = useMemo(
    () => candidates.filter((c) => matchedSet.has(c.symbol)),
    [candidates, matchedSet]
  );

  const handleAddCandidate = (symbol: string) => {
    if (form.includeSymbols.includes(symbol)) return; // 已在列表里（重复点击）——不必再打一次预览
    setForm((prev) => ({ ...prev, includeSymbols: [...prev.includeSymbols, symbol] }));
    setRefreshNonce((n) => n + 1);
  };

  const handleRemoveManual = (symbol: string) => {
    setForm((prev) => ({ ...prev, includeSymbols: prev.includeSymbols.filter((s) => s !== symbol) }));
    // 同时乐观地把它移出 matchedSymbols：右侧列表已按 includeSymbols 立刻移除该行，若这里不动，
    // 左侧「可选」仍会因为过期的 matchedSymbols 把它挡住，于是它两边都不在。刷新失败时这个
    // 状态会一直卡住（matchedSymbols 只在成功响应里更新），所以不能只依赖下一次请求来纠正。
    // 如果它其实还命中规则，下一次成功刷新会把它放回 matchedSymbols 和右侧列表（source=rule），
    // 代价只是短暂出现在左侧——单边出现，永远好过两边都消失。
    setMatchedSymbols((prev) => prev.filter((s) => s !== symbol));
    setRefreshNonce((n) => n + 1);
  };

  // Swaps two symbols' positions within `includeSymbols` directly (rather than assuming adjacent
  // array indices) so a reorder only ever affects the two active manual rows involved, regardless
  // of where any delisted entries happen to sit in the underlying array.
  const handleMoveManual = (symbol: string, direction: -1 | 1) => {
    const index = activeManualSymbols.indexOf(symbol);
    const neighborIndex = index + direction;
    if (index < 0 || neighborIndex < 0 || neighborIndex >= activeManualSymbols.length) return;
    const neighbor = activeManualSymbols[neighborIndex];
    setForm((prev) => {
      const next = [...prev.includeSymbols];
      const ia = next.indexOf(symbol);
      const ib = next.indexOf(neighbor);
      if (ia < 0 || ib < 0) return prev;
      [next[ia], next[ib]] = [next[ib], next[ia]];
      return { ...prev, includeSymbols: next };
    });
    setRefreshNonce((n) => n + 1);
  };

  const handleSave = async () => {
    if (!view) return;
    setSaving(true);
    try {
      const body = buildViewBody(form);
      await viewsApi.update(view.code, body);
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // 这个弹窗是双栏 + 实时预览表格，md 宽度下两栏都被挤窄、预览列要横向滚动。
      // 拉到 xl 并给一个高度下限，让两栏和预览表格都能一屏看完。
      PaperProps={{ sx: { minHeight: '85vh' } }}
    >
      <DialogTitle>
        {translate('module.perpsV1Views.manageSymbolsTitle', {
          name: view ? displayLabel(view.label, view.code) : '',
        })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">{translate('module.perpsV1Views.fields.dexes')}</Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              {dexRows.map((row) => (
                <FormControlLabel
                  key={row.id}
                  control={
                    <Checkbox
                      checked={form.dexes.includes(row.dex)}
                      onChange={(e) => toggleDex(row.dex, e.target.checked)}
                    />
                  }
                  label={row.dex === NATIVE_DEX ? translate('module.perpsV1Views.fields.native') : row.dex}
                />
              ))}
            </Stack>
          </Stack>

          <Autocomplete
            multiple
            size="small"
            options={sectorOptions}
            value={selectedSectorOptions}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            getOptionLabel={(option) => option.label}
            onChange={(_, next) => set('sectorCodes', next.map((o) => o.code))}
            renderInput={(params) => (
              <TextField {...params} label={translate('module.perpsV1Views.fields.sectors')} />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={translate('module.perpsV1Views.fields.listingWindowDays')}
                value={form.listingWindowDays}
                onChange={(e) => set('listingWindowDays', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={translate('module.perpsV1Views.fields.minVolume')}
                value={form.minVolume}
                onChange={(e) => set('minVolume', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label={translate('module.perpsV1Views.fields.minChangePercent')}
                value={form.minChangePercent}
                onChange={(e) => set('minChangePercent', e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                label={translate('module.perpsV1Views.fields.sortField')}
                value={form.sortField}
                onChange={(e) => set('sortField', e.target.value)}
                // 仅 listed_at 有并列（同天上线）打平规则，且与排序方向无关——这条提示只对
                // 这一个选项有意义，其余四项常年显示反而是噪音，所以只在选中它时才渲染。
                helperText={
                  form.sortField === 'listed_at'
                    ? translate('module.perpsV1Views.sortFieldListedAtHint')
                    : undefined
                }
              >
                {SORT_FIELDS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {translate(SORT_FIELD_LABEL_KEYS[f] ?? f)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                label={translate('module.perpsV1Views.fields.sortDirection')}
                value={form.sortDirection}
                onChange={(e) => set('sortDirection', e.target.value)}
              >
                {SORT_DIRECTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {translate(SORT_DIRECTION_LABEL_KEYS[d] ?? d)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">
                {translate('module.perpsV1Views.membersInclude')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {translate('module.perpsV1Views.membersIncludeHint')}
              </Typography>
            </Stack>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <ViewAvailableSymbols
                candidates={availableCandidates}
                loading={candidatesLoading}
                onAdd={handleAddCandidate}
              />
              <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">
                  {translate('module.perpsV1Views.previewSection')}
                </Typography>
                {delistedManual.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 1.25, borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {translate('module.perpsV1Views.membersDelistedHint')}
                    </Typography>
                    <Stack spacing={1}>
                      {delistedManual.map((symbol) => (
                        <Stack key={symbol} direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            color="warning"
                            label={translate('module.perpsV1Views.membersDelisted')}
                          />
                          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {symbol}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveManual(symbol)}
                            aria-label={translate('module.perpsV1Views.membersRemove')}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
                <ViewPreviewPanel
                  body={previewBody}
                  triggerKey={previewTriggerKey}
                  refreshNonce={refreshNonce}
                  disabled={!form.code.trim()}
                  disabledReason="module.perpsV1Views.previewNeedsCode"
                  manualSymbols={activeManualSymbols}
                  candidates={candidates}
                  onResult={({ matchedSymbols: next }) => setMatchedSymbols(next)}
                  onRemoveManual={handleRemoveManual}
                  onMoveManual={handleMoveManual}
                />
              </Stack>
            </Box>
          </Stack>

          <Accordion
            // 默认展开：Exclude 是这个弹窗里唯一能把规则命中的币种移出榜单的入口，
            // 折叠起来管理员根本找不到它（预览行的提示语也指向这里）。
            defaultExpanded
            disableGutters
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
              <Typography variant="subtitle2">
                {translate('module.perpsV1Views.membersAdvanced')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">
                    {translate('module.perpsV1Views.membersExclude')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('module.perpsV1Views.membersExcludeHint')}
                  </Typography>
                </Stack>
                <ViewMembersEditor
                  value={form.excludeSymbols}
                  onChange={(next) => set('excludeSymbols', next)}
                  candidates={excludeCandidates}
                  loading={candidatesLoading}
                  candidatesUnavailable={candidatesUnavailable}
                  orderable={false}
                  labelKey="module.perpsV1Views.membersSelected"
                  emptyKey="module.perpsV1Views.membersEmptyExclude"
                  unavailableSymbols={form.includeSymbols}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={saving}>
          {translate('module.perpsV1Shared.cancel')}
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={!form.sortField || !form.sortDirection}
        >
          {translate('module.perpsV1Shared.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
